/*!
* Define multiband compressor properties and behaviour.
*/

export default class MultibandCompressor {
	/* DEFAULT LOGIC (METHODS) THAT SHOULD BE IMPLEMENTED IN EACH PLUGIN */

	//Used for getting current values for save
	getValues() {
		let values = {};

		values.hzBandSplits = this.hzBandSplits;

		for (let index = 1; index <= this.hzBands; index++) {
			values['band' + index] = {};
			values['band' + index]['preGainSettings'] = this['band' + index]['preGainSettings'];
			values['band' + index]['dcSettings'] = this['band' + index]['dcSettings'];
			values['band' + index]['makeupGainSettings'] = this['band' + index]['makeupGainSettings'];
			values['band' + index]['solo'] = this['band' + index]['solo'];
		}
		values.masterGainSettings = this.masterGainSettings;

		return values;
	}

	// Used for setting stored values.
	// For each value, we check to see if it was passed in the 
	// values parameter before setting it to a default value
	// We'll do the same thing with a ternary for all the properties.
	setValues(values) {
		if (typeof values == 'undefined' || Object.keys(values).length === 0) { // if null or empty object passed

			// Set all settings to defaults
			for (let index = 1; index <= this.hzBands; index++) {
				this['band' + index] = {};

				// The default preGain settings for each frequency band
				this['band' + index]['preGainSettings'] = {
					gain: this.gain
				}

				// The default compressor settings for each frequency band
				this['band' + index]['dcSettings'] = {
					threshold: this.threshold,
					ratio: this.ratio,
					knee: this.knee,
					attack: this.attack,
					release: this.release,
				};

				this['band' + index]['solo'] = 0;
				this['band' + index]['makeupGainSettings'] = {};
				this['band' + index]['makeupGainSettings'].gain = this.makeupGain;

				this['band' + index + 'Att'] = 0;
			}

			// Master gain
			this.masterGainSettings = {
				gain: this.masterGainSettings.gain,
				inputGain: this.masterGainSettings.inputGain,
				outputGain: this.masterGainSettings.outputGain
			};
		} else {
			if (this.isDebug) {
				console.debug('Set multiband values', { values, initialized: this.initialized });
			}

			// Set band splits
			this.updateHzBandSplits(0, values.hzBandSplits[0])
			this.updateHzBandSplits(1, values.hzBandSplits[1])

			for (let index = 1; index <= this.hzBands; index++) {
				// Try to get the band settings from the values object. If a specific property wasn't passed, 
				// set it to an empty object.

				//COMMENT: it is wrong to overwrite this['band' + index] object. It contains dcNode, if playing, so it would be overwritten;
				//this['band' + index] = values.hasOwnProperty('band' + index) ? values['band' + index] : {};
				if (!this['band' + index])
					this['band' + index] = values.hasOwnProperty('band' + index) ? values['band' + index] : {};

				// Check for the preGainSettings property. If it's there use it, otherwise set an empty object.
				this['band' + index]['preGainSettings'] = values['band' + index].hasOwnProperty('preGainSettings') ? values['band' + index]['preGainSettings'] : {};
				let passedPreGainSettings = values['band' + index]['preGainSettings']; // Put them in a variable for readability of the ternaries
				// Set the preGain settings
				this['band' + index]['preGainSettings']['gain'] = this.isEmpty(passedPreGainSettings, 'gain') ? this.threshold : passedPreGainSettings['gain'];

				// Check for the dcSettings property. If it's there use it, otherwise set an empty object.
				this['band' + index]['dcSettings'] = values['band' + index].hasOwnProperty('dcSettings') ? values['band' + index]['dcSettings'] : {};
				// Set the compressor settings
				let passedDcSettings = values['band' + index]['dcSettings']; // Put them in a variable for readability of the ternaries
				this['band' + index]['dcSettings'] = {
					threshold: this.isEmpty(passedDcSettings, 'threshold') ? this.threshold : passedDcSettings['threshold'],
					ratio: this.isEmpty(passedDcSettings, 'ratio') ? this.ratio : passedDcSettings['ratio'],
					knee: this.isEmpty(passedDcSettings, 'knee') ? this.knee : passedDcSettings['knee'],
					attack: this.isEmpty(passedDcSettings, 'attack') ? this.attack : passedDcSettings['attack'],
					release: this.isEmpty(passedDcSettings, 'release') ? this.release : passedDcSettings['release']
				};

				// Check for the makeup gain property. If it's there use it, otherwise set an empty object.
				this['band' + index]['makeupGainSettings'] = values['band' + index].hasOwnProperty('makeupGainSettings') ? values['band' + index]['makeupGainSettings'] : {};
				// Makeup gain settings
				let passedMakeupGainSettings = values['band' + index]['makeupGainSettings'];
				this['band' + index]['makeupGainSettings'].gain = this.isEmpty(passedMakeupGainSettings, 'gain') ? this.makeupGain : passedMakeupGainSettings['gain'];

				//COMMENT: setValues should not only set defaults to variables, but also set values to audio nodes, if initialized
				this.updateThreshold(index, this['band' + index]['dcSettings']['threshold']);
				this.updateRatio(index, this['band' + index]['dcSettings']['ratio']);
				this.updateAttack(index, this['band' + index]['dcSettings']['attack']);
				this.updateRelease(index, this['band' + index]['dcSettings']['release']);
				this.updateMakeupGain(index, this['band' + index]['makeupGainSettings']['gain']);
				this.handleSolo(index, values['band' + index]['solo']);

				this['band' + index + 'Att'] = 0;
			}

			// Check for the master gain property. If it's there use it, otherwise set an empty object.
			this['masterGainSettings'] = values.hasOwnProperty('masterGainSettings') ? values['masterGainSettings'] : {};

			// Master gain
			let passedMasterGainSettings = values.masterGainSettings;
			this.masterGainSettings.gain = this.isEmpty(passedMasterGainSettings, 'gain') ? this.masterGainSettings.gain : passedMasterGainSettings['gain'];
			this.masterGainSettings.inputGain = this.isEmpty(passedMasterGainSettings, 'inputGain') ? this.masterGainSettings.inputGain : passedMasterGainSettings['inputGain'];
			this.masterGainSettings.outputGain = this.isEmpty(passedMasterGainSettings, 'outputGain') ? this.masterGainSettings.outputGain : passedMasterGainSettings['outputGain'];

			//COMMENT: setValues should not only set defaults to variables, but also set values to audio nodes, if initialized
			this.updateMasterGain(this.masterGainSettings.gain);
			this.updateInputGain(this.masterGainSettings.inputGain);
			this.updateOutputGain(this.masterGainSettings.outputGain);
		}
	}

	//Rows: array of rows (visually each row will be stacked vertically), inside array of elements - they will be stacked horizontally in a row - affectors (value changers) or indicators
	//Availale kinds: 'indicator', 'affector'
	//Available types for affectors: 'round-button' (min, max, step), 'slider' (min, max, step),
	//Available types for indicators: 'vu-meter' (min, max, orientation(vertical, horizontal))
	//Indicator can show values in pure value (like db) or percent, but better in pure value. Examples: { value: -15 }, { percent: 50 }
	getUI() {
		let ui = {
			rows: [],
			width: 960,
			height: 560
		};

		let bandSplitRows = [
			{
				kind: 'affector', type: 'round-button', width: 50, color: '#fe4b4b', title: 'Hz split 1', min: 20, max: 2000, step: 1, round: 1,
				setValue: (value) => { return this.updateHzBandSplits(0, value) },
				getValue: () => { return this.hzBandSplits[0] }
			},
			{
				kind: 'affector', type: 'round-button', width: 50, color: '#fe4b4b', title: 'Hz split 2', min: 20, max: 20000, step: 10, round: 1,
				setValue: (value) => { return this.updateHzBandSplits(1, value) },
				getValue: () => { return this.hzBandSplits[1] }
			}
		];

		let compressorRows = [];
		let colors = ['#4dffae', '#73b1e3', '#c495fd']
		for (let index = 1; index <= this.hzBands; index++) {
			let propertyRows = [
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[index - 1], title: 'Threshold', min: this.minThreshold, max: this.maxThreshold, step: 1, unit: 'dB',
					setValue: (value) => { return this.updateThreshold(index, value) },
					getValue: () => { return this['band' + index].dcSettings.threshold }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[index - 1], title: 'Ratio', min: this.minRatio, max: this.maxRatio, step: 1,
					setValue: (value) => { return this.updateRatio(index, value) },
					getValue: () => { return this['band' + index].dcSettings.ratio }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[index - 1], title: 'Attack', min: this.minAttack, max: this.maxAttack, step: 1, unit: 'ms',
					setValue: (value) => { return this.updateAttack(index, value) },
					getValue: () => { return this['band' + index].dcSettings.attack }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, color: colors[index - 1], title: 'Release', min: this.minRelease, max: this.maxRelease, step: 1, unit: 'ms',
					setValue: (value) => { return this.updateRelease(index, value) },
					getValue: () => { return this['band' + index].dcSettings.release }
				},
				{
					kind: 'affector', type: 'round-button', width: 75, title: 'Gain', min: -30, max: 6, step: .1, round: 1, unit: 'dB',
					setValue: (value) => { return this.updateMakeupGain(index, value) },
					getValue: () => { return this['band' + index].makeupGainSettings.gain }
				},
				{
					kind: 'affector', type: 'switch', width: 50, color: 'yellow', title: 'Solo', min: 0, max: 1, step: 1, round: 1,
					setValue: (value) => { return this.handleSolo(index, value) },
					getValue: () => { return this['band' + index].solo }
				},
				{
					kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Attenuation ' + index, min: 0, max: 20, redPoint: 18, yellowPoint: 15, getValue: () => {
						return { value: this['band' + index + 'Att'] }
					}
				}
			];
			compressorRows.push(propertyRows);
			if (index < 3) {
				compressorRows.push([
					{
						kind: 'indicator',
						type: 'divider',
						getValue: () => {
							const freq = this.hzBandSplits[index-1];
							return `${freq > 1000 ? parseInt(freq / 10) / 100 : freq} ${freq > 1000 ? 'kHz' : 'Hz'}`;
						},
						color: colors[index - 1], 
						title: 'Frequency',
						height: '60px'
					},
				])
			}
		}

		let masterGainRows = [
			{
				kind: 'affector', type: 'round-button', width: 75, title: 'Master Gain', min: -60, max: 6, step: .1, round: 1, unit: 'dB',
				setValue: (value) => { return this.updateMasterGain(value) },
				getValue: () => { return this.masterGainSettings.gain }
			},
			{
				kind: 'affector', type: 'round-button', width: 75, title: 'Input gain', min: -60, max: 6, step: .1, round: 1, unit: 'dB',
				setValue: (value) => { return this.updateInputGain(value) },
				getValue: () => { return this.masterGainSettings.inputGain }
			},
			{
				kind: 'affector', type: 'round-button', width: 75, title: 'Output gain', min: -60, max: 6, step: .1, round: 1, unit: 'dB',
				setValue: (value) => { return this.updateOutputGain(value) },
				getValue: () => { return this.masterGainSettings.outputGain }
			},
			...bandSplitRows
		];

		ui.rows.push([
			{
				rows: [
					/*qualityRow,*/
					...compressorRows
				]
			},
			{
				rows: [
					masterGainRows
				],
				orientation: 'vertical'
			},
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'In', min: -60, max: 3, getValue: () => {
					return {
						value: this.preMetering
					}
				}
			},
			{
				kind: 'indicator', type: 'vu-meter', orientation: 'vertical', title: 'Out', min: -60, max: 3, getValue: () => {
					return {
						value: this.postMetering
					}
				}
			}
		]);
		return ui;
	}


	/**
	 * Create nodes and connect them
	 * 
	 * @returns object containing input and output node
	 */
	getAudioPipeline(opts) {
		this.ctx = opts?.audioContext || new AudioContext();

		// Create pre analyser node
		this.preAnalyserNode = this.ctx.createAnalyser(); // The input audio analyser

		// Input gain node
		this.inputGainNode = this.ctx.createGain();

		// Output gain node
		this.outputGainNode = this.ctx.createGain();

		// Master gain node
		this.masterGainNode = this.ctx.createGain();

		// Create post analyser node
		this.postAnalyserNode = this.ctx.createAnalyser(); // The output audio analyser

		// Create common nodes for each band
		for (let index = 1; index <= this.hzBands; index++) {
			// Create the pre gain node to this band. For solo/mute
			this['band' + index].preGainNode = this.ctx.createGain();
			// Create the compressor node
			this['band' + index].dcNode = this.ctx.createDynamicsCompressor();
			// Create the makeup gain node 
			this['band' + index].makeupGainNode = this.ctx.createGain();
			// Create the post compressor analyser
			this['band' + index].analyserNode = this.ctx.createAnalyser();
		}

		/**
		 * Create filter nodes - different for each band
		 */

		// The low frequencies
		this.band1.lowpassFilterNode = this.ctx.createBiquadFilter();
		this.band1.invertNode = this.ctx.createGain();

		// The medium frequencies
		this.band2.gainNode = this.ctx.createGain();

		// The high frequencies
		this.band3.highpassFilterNode = this.ctx.createBiquadFilter();
		this.band3.invertNode = this.ctx.createGain();

		this.preAnalyserNode.connect(this.inputGainNode);

		/**
		 * Connect nodes
		 */
		// Band 1

		this.inputGainNode.connect(this.band1.lowpassFilterNode);
		this.band1.lowpassFilterNode.connect(this.band1.preGainNode);
		this.band1.preGainNode.connect(this.band1.dcNode);
		this.band1.dcNode.connect(this.band1.makeupGainNode);
		this.band1.makeupGainNode.connect(this.band1.analyserNode);
		this.band1.analyserNode.connect(this.masterGainNode);

		// Band 2
		this.inputGainNode.connect(this.band2.gainNode);
		// Invert low/high
		this.band3.highpassFilterNode.connect(this.band3.invertNode);
		this.band1.lowpassFilterNode.connect(this.band1.invertNode);
		// Add (subtract) them to get the medium frequencies
		this.band3.invertNode.connect(this.band2.gainNode);
		this.band1.invertNode.connect(this.band2.gainNode);
		this.band2.gainNode.connect(this.band2.preGainNode);

		this.band2.preGainNode.connect(this.band2.dcNode);
		this.band2.dcNode.connect(this.band2.makeupGainNode);
		this.band2.makeupGainNode.connect(this.band2.analyserNode);
		this.band2.analyserNode.connect(this.masterGainNode);

		// Band 3
		this.inputGainNode.connect(this.band3.highpassFilterNode);
		this.band3.highpassFilterNode.connect(this.band3.preGainNode);
		this.band3.preGainNode.connect(this.band3.dcNode);
		this.band3.dcNode.connect(this.band3.makeupGainNode);
		this.band3.makeupGainNode.connect(this.band3.analyserNode);
		this.band3.analyserNode.connect(this.masterGainNode);

		// Master gain
		this.masterGainNode.connect(this.outputGainNode);
		this.outputGainNode.connect(this.postAnalyserNode);

		this.inputNode = this.preAnalyserNode;
		this.outputNode = this.postAnalyserNode;

		this.init();

		// To input will be connected source (with other effects), output will be connected to destination (or next effect)
		return {
			input: this.preAnalyserNode,
			output: this.postAnalyserNode
		}
	}

	// Called on playing state change (1 - playing, 0 - stop)
	setPlayState(value) {
		this.initialized = !!value;
		this.playing = !!value;

		if (this.isDebug) {
			console.debug('Set play state changed', { value });
		}

		if (value)
			this.updateMeters();
	}

	constructor(opts) {
		this.ctx = opts.context;
		this.player = opts.player;

		// Show console messages
		this.isDebug = opts.isDebug;

		if (this.isDebug) {
			console.log('Creating multiband compressor... ');
		}

		this.connected = false;

		// Set frequency static band splits
		this.hzBandSplits = [200, 2000];
		this.hzBands = 3;

		// Pre gain for solo/mute
		this.bandPreGain = 1;

		// Filter node
		this.filter;
		this.Q = 1;

		// Default compressor settings for each band
		this.threshold = 0;
		this.knee = 30;
		this.ratio = 1;
		this.attack = 1;
		this.release = 1;

		// Default gain
		this.gain = 1;
		this.inputGain = 1;
		this.outputGain = 1;

		// Default makeup gain in Db
		this.makeupGain = 0;

		// Default max input values
		this.maxThreshold = 0; // Max value of threshold range sliders
		this.maxRatio = 20;
		this.maxAttack = 1000; // In ms
		this.maxRelease = 2000; // In ms
		this.maxGain = 5;

		// Default min inupt values
		this.minThreshold = -30; // Min value of threshold range sliders
		this.minRatio = 1;
		this.minAttack = 1; // In seconds
		this.minRelease = 1; // In seconds
		this.minGain = 0;

		// Master gain defaults in Db
		this.masterGainSettings = {
			gain: 0,
			inputGain: 0,
			outputGain: 0
		};

		// Analyser properties
		this.fttSize = 2048; // bytes to allocate for data
		this.preMetering = null;
		this.postMetering = null;

		this.initialized = false;

		this.preInit();
	}

	/* PLUGIN LOGIC */

	// Add some basic functionality for before the user is playing an audio file
	preInit() {
		// Test setting values
		// let values = '{"band1":{"preGainSettings":{"gain":1},"dcSettings":{"threshold":-2,"ratio":4,"knee":30,"attack":0.01,"release":0.1},"makeupGainSettings":{"gain":2}},"band2":{"preGainSettings":{"gain":1},"dcSettings":{"threshold":0,"ratio":1,"knee":30,"attack":0.01,"release":0.1},"makeupGainSettings":{"gain":1}},"band3":{"preGainSettings":{"gain":1},"dcSettings":{"threshold":0,"ratio":1,"knee":30,"attack":0.20,"release":0.9},"makeupGainSettings":{"gain":1}},"masterGainSettings":{"gain":1}}';
		// this.setValues(JSON.parse(values));
		this.setValues();
	}

	// This is the last second logic that needs to execute for the plugin to work
	init() {
		this.initNodes(); // Use the values on compressor object to set values of the nodes

		this.initPreAnalyser();
		this.initPostAnalyser();
		// console.log(JSON.stringify(this.getValues()));

		this.initialized = true;
	}

	// Initialize values in nodes
	initNodes() {
		// Check if any bands are soloed
		let soloedBands = [];
		for (let index = 1; index <= this.hzBands; index++) {
			if (this['band' + index].solo === 1)
				soloedBands.push(index);
		}

		// Initialize compressor nodes for each band
		for (let index = 1; index <= this.hzBands; index++) {
			// If any bands are soloed, they should have gain default and others should have gain 0
			if (soloedBands.length) {
				if (soloedBands.includes(index)) {
					// Initialize band pregain node
					this['band' + index].preGainNode.gain.setValueAtTime(
						this['band' + index].preGainSettings.gain,
						this.ctx.currentTime
					);
				} else {
					// Initialize band pregain node to 0 - not soloed
					this['band' + index].preGainNode.gain.setValueAtTime(
						0,
						this.ctx.currentTime
					);
				}
				// Otherwise they should all be default
			} else {
				// Initialize band pregain node
				this['band' + index].preGainNode.gain.setValueAtTime(
					this['band' + index].preGainSettings.gain,
					this.ctx.currentTime
				);
			}

			// Initialize compressor node settings
			this['band' + index].dcNode.threshold.value = this['band' + index].dcSettings.threshold;
			this['band' + index].dcNode.knee.value = this['band' + index].dcSettings.knee;
			this['band' + index].dcNode.ratio.value = this['band' + index].dcSettings.ratio;
			this['band' + index].dcNode.attack.value = this['band' + index].dcSettings.attack;
			this['band' + index].dcNode.release.value = this['band' + index].dcSettings.release;

			// Initialize makeup gain node settings
			this['band' + index].makeupGainNode.gain.setValueAtTime(
				this['band' + index].makeupGainSettings.gain,
				this.ctx.currentTime
			);

			// Initialize post compressor analyser settings
			this['band' + index].analyserNode.fftSize = this.fttSize;
			this['band' + index].bandFloatTimeData = new Float32Array(
				this['band' + index].analyserNode.frequencyBinCount
			);
			this['band' + index].analyserNode.getFloatTimeDomainData(
				this['band' + index].bandFloatTimeData
			);
		}

		// Filter settings
		// The high frequencies
		this.band3.highpassFilterNode.type = "highpass";
		this.band3.highpassFilterNode.frequency.value = this.hzBandSplits[1];
		this.band3.highpassFilterNode.Q.value = this.Q;

		this.band3.invertNode.gain.setValueAtTime(-1.0, this.ctx.currentTime);

		// The medium frequencies are what's in between the high and low

		// The low frequencies
		this.band1.lowpassFilterNode.type = "lowpass";
		this.band1.lowpassFilterNode.frequency.value = this.hzBandSplits[0];
		this.band1.lowpassFilterNode.Q.value = this.Q;

		this.band1.invertNode.gain.setValueAtTime(-1.0, this.ctx.currentTime);

		// Initialize pre analyser node
		this.preAnalyserNode.fftSize = this.fttSize;
		this.preFloatTimeData = new Float32Array(
			this.preAnalyserNode.frequencyBinCount
		);
		this.preAnalyserNode.getFloatTimeDomainData(
			this.preFloatTimeData
		);

		// Initialize post analyser node
		this.postAnalyserNode.fftSize = this.fttSize;
		this.postFloatTimeData = new Float32Array(
			this.postAnalyserNode.frequencyBinCount
		);
		this.postAnalyserNode.getFloatTimeDomainData(
			this.postFloatTimeData
		);

		// Initialize master gain node value
		this.masterGainNode.gain.setValueAtTime(
			this.masterGainSettings.gain,
			this.ctx.currentTime
		);
	}

	initPreAnalyser() {
		// Analyzer for metering
		this.preAnalyserNode.fftSize = this.fttSize;

		this.preFloatTimeData = new Float32Array(this.preAnalyserNode.frequencyBinCount);
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);
	}

	initPostAnalyser() {
		// Analyzer for metering
		this.postAnalyserNode.fftSize = this.fttSize;

		this.postFloatTimeData = new Float32Array(this.postAnalyserNode.frequencyBinCount);
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);
	}

	/**
	 * Bypass the plugin
	 */
	bypass() {
		if (this.$bypass.attr('data-bypassed') == 'true') {
			this.connected = true;
			this.$bypass.text('On').attr('data-bypassed', 'false');
		} else {
			this.connected = false;
			this.$bypass.text('Off').attr('data-bypassed', 'true');
		}
	}

	updateHzBandSplits(split, frequency) {
		this.hzBandSplits[split] = frequency;

		if (!this.initialized)
			return; // If there aren't any nodes, don't try to set their values

		// Update the high frequencies
		this.band3.highpassFilterNode.frequency.value = this.hzBandSplits[1];
		this.band3.invertNode.gain.setValueAtTime(-1.0, this.ctx.currentTime);

		// Update the low frequencies
		this.band1.lowpassFilterNode.frequency.value = this.hzBandSplits[0];
		this.band1.invertNode.gain.setValueAtTime(-1.0, this.ctx.currentTime);
	}

	handleSolo(band, value) {
		if (__DEBUG__)
			console.debug('handleSolo', { band, value });
		this['band' + band].solo = value;

		if (!this.initialized)
			return;

		// Check if any bands are soloed
		let soloedBands = [];
		for (let index = 1; index <= this.hzBands; index++) {
			if (this['band' + index].solo)
				soloedBands.push(index);
		}

		// If any bands are soloed, set their gain to 0 and others to default
		for (let index = 1; index <= this.hzBands; index++) {
			if (soloedBands.length) {
				if (soloedBands.includes(index)) {
					// Initialize band pregain node
					this['band' + index].preGainNode.gain.setValueAtTime(
						this['band' + index].preGainSettings.gain,
						this.ctx.currentTime
					);
				} else {
					// Initialize band pregain node to 0 - not soloed
					this['band' + index].preGainNode.gain.setValueAtTime(
						0,
						this.ctx.currentTime
					);
				}
			} else {
				// Initialize band pregain node
				this['band' + index].preGainNode.gain.setValueAtTime(
					this['band' + index].preGainSettings.gain,
					this.ctx.currentTime
				);
			}
		}
	}

	updateThreshold(band, value) {
		this['band' + band].dcSettings.threshold = value;

		if (__DEBUG__)
			console.debug('Update threshold', { band, value, b: this['band' + band], init: this.initialized });

		if (!this.initialized)
			return;

		// Update the band's compressor node threshold value
		this['band' + band].dcNode.threshold.setValueAtTime(this['band' + band].dcSettings.threshold, this.ctx.currentTime);
	}

	updateRatio(band, value) {
		this['band' + band].dcSettings.ratio = value;

		if (!this.initialized)
			return;

		// Update the band's compressor node ratio value
		this['band' + band].dcNode.ratio.setValueAtTime(this['band' + band].dcSettings.ratio, this.ctx.currentTime);
	}

	updateAttack(band, value) {
		this['band' + band].dcSettings.attack = value;

		if (!this.initialized)
			return;

		// Update the band's compressor node ratio value
		this['band' + band].dcNode.attack.setValueAtTime(this['band' + band].dcSettings.attack / 1000, this.ctx.currentTime);
	}

	updateRelease(band, value) {
		this['band' + band].dcSettings.release = value;

		if (!this.initialized)
			return;

		// Update the band's compressor node ratio value
		this['band' + band].dcNode.release.setValueAtTime(this['band' + band].dcSettings.release / 1000, this.ctx.currentTime);
	}

	updateMakeupGain(band, value) {
		this['band' + band].makeupGainSettings.gain = value;

		if (!this.initialized)
			return;

		// Update the band's compressor node ratio value
		this['band' + band].makeupGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateMasterGain(value) {
		this.masterGainSettings.gain = value;

		if (!this.initialized)
			return;

		// Update the node value
		this.masterGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateInputGain(value) {
		this.masterGainSettings.inputGain = value;

		if (!this.initialized)
			return;

		// Update the node value
		this.inputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	updateOutputGain(value) {
		this.masterGainSettings.outputGain = value;

		if (!this.initialized)
			return;

		// Update the node value
		this.outputGainNode.gain.setValueAtTime(this.convertDecibelsToGain(value), this.ctx.currentTime);
	}

	// Check if the object contains the property and
	// and the property is not null or empty
	isEmpty(object, property) {
		if (object.hasOwnProperty(property)) return false;
		return true;
	}

	// The meters are acting as peak meters
	updateMeters() {
		// Schedule the next update
		if (this.playing) {
			requestAnimationFrame(() => this.updateMeters()); // Recursive
		}

		this.updatePreMeters();
		this.updatePostMeters();
		this.updateAttenuationMeters();
	}

	updateAttenuationMeters() {
		// Set attenuation meter for each band
		for (let index = 1; index <= this.hzBands; index++) {
			this['band' + index + 'Att'] = Math.abs(this['band' + index]['dcNode']['reduction']);
		}

	}

	updatePreMeters() {
		// Get the new frequency data
		this.preAnalyserNode.getFloatTimeDomainData(this.preFloatTimeData);

		let sampleBuffer = this.preFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.preMetering = peakInstantaneousPowerDecibels;
	}

	updatePostMeters() {
		// Get the new frequency data
		this.postAnalyserNode.getFloatTimeDomainData(this.postFloatTimeData);

		let sampleBuffer = this.postFloatTimeData;
		// Compute peak instantaneous power over the interval.
		let peakInstantaneousPower = 0;
		for (let i = 0; i < sampleBuffer.length; i++) {
			const power = sampleBuffer[i] ** 2;
			peakInstantaneousPower = Math.max(power, peakInstantaneousPower);
		}
		const peakInstantaneousPowerDecibels = this.convertGainToDecibels(peakInstantaneousPower);

		this.postMetering = peakInstantaneousPowerDecibels;
	}

	convertDecibelsToGain(db) {
		return Math.pow(10, (db / 20));
	}

	convertGainToDecibels(gain) {
		return 20 * Math.log10(gain);
	}
}